import { Fragment, useEffect, useRef, useState } from 'react'
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  IconButton,
  Menu,
  Toolbar,
  Tooltip,
  Fade,
  styled,
  alpha,
  InputBase,
  Divider,
  Typography
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import PersonIcon from '@mui/icons-material/Person'
import SearchIcon from '@mui/icons-material/Search'
import { makeStyles } from '@mui/styles'
import Swal from 'sweetalert2'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/Auth/AuthContext'
import { useConfig } from '../../context/Config/ConfigContext'
import { useProducts } from '../../context/Products/ProductsContext'
import { useFilter } from '../../context/FilterContext/FilterContext'
import { useCart } from '../../context/Cart/CartContext'
import CartWidget from '../../components/CartWidget'
import MobileMenu from '../../components/MobileMenu'
import logo from '../../assets/img/logos/logo-grupo-monaco.jpg'
import Offset from '../Offset'
import './Navbar.css'
import { formatter } from '../../utils/auxFunc'

const SearchWrapper = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25)
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('md')]: {
    marginLeft: theme.spacing(1),
    width: 'auto'
  }
}))

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}))

const SearchInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '12ch',
      '&:focus': {
        width: '18ch'
      }
    }
  }
}))

const Navbar = () => {
  const [anchorElNav, setAnchorElNav] = useState(null)
  const [expanded, setExpanded] = useState(false)
  const [search, setSearch] = useState('')
  const [showOffset, setShowOffset] = useState(false)
  const navigate = useNavigate()
  const { offset, getNavbarOffset } = useConfig()
  const { config } = useConfig()
  const { categories } = useProducts()
  const { resetFilter } = useFilter()
  const { cart, switchSideCart } = useCart()
  const { userData } = useAuth()
  let toolbarH = useRef(null)

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false)
  }

  const sendSearch = (evt) => {
    if (evt.key === 'Enter') {
      if (search.length < 3) {
        return Swal.fire({
          customClass: {
            confirmButton: 'swalBtnOk'
          },
          position: 'center',
          icon: 'error',
          title: 'Hubo un problema',
          text: 'Debe ingresar al menos 3 caracteres',
          showConfirmButton: true,
          focusConfirm: false,
          iconColor: '#FF8A65',
          color: '#FAFAFA',
          background: '#292929',
          confirmButtonColor: '#292929'
        })
      }
      navigate(`/search/${search}`)
      setSearch('')
      window.innerWidth < 900 && toggleNavMenu()
    }
  }

  const handleSearch = (evt) => {
    setSearch(evt.target.value)
  }

  // Solo sirve para el menu con estilo mobile
  const toggleNavMenu = (event) => {
    anchorElNav ? setAnchorElNav(null) : setAnchorElNav(event.currentTarget)
    setExpanded(false)
  }

  const useStyles = makeStyles((theme) => ({
    // seteo de margen superior en función del tamaño del navbar responsive
    offset: {
      minHeight: offset && `${offset}px`
    },
    [theme.breakpoints.down('md')]: {
      offset: {
        minHeight: `calc(${theme.mixins.toolbar.minHeight}px + 38px)`
      }
    },
    appbar: {
      backgroundColor: theme.palette.primary.main
    }
  }))

  const classes = useStyles()

  useEffect(() => {
    setShowOffset(true)
    getNavbarOffset(toolbarH.current.clientHeight)
  }, [categories, getNavbarOffset])

  return (
    <Fragment>
      <AppBar position="fixed" className={classes.appbar} ref={toolbarH}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-around',
            flexWrap: 'wrap',
            alignItems: 'center',
            backgroundColor: (theme) => theme.palette.brand.main
          }}
          py={1}
        >
          <Typography
            variant="h6"
            color="primary"
            textAlign="center"
            sx={{ fontSize: '12px', padding: '0 1rem' }}
          >
            {config?.shipmentInfo &&
              (config.shipmentInfo.monto !== 0
                ? `${config.shipmentInfo.descrp} ${formatter(0).format(
                    config.shipmentInfo.monto
                  )}`.toUpperCase()
                : `${config.shipmentInfo.descrp}`.toUpperCase())}
          </Typography>
        </Box>
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              position: 'relative'
            }}
          >
            <Box display="flex" flexDirection="column" alignItems="center">
              <Box pt={1} pb={1} width="100%" sx={{ height: '70px' }}>
                <Box
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    margin: '0 auto',
                    width: 'fit-content',
                    minWidth: '120px'
                  }}
                >
                  <Link to="/">
                    <Avatar
                      alt="Logo de Grupo Monaco"
                      variant="square"
                      src={logo}
                      className="navbarLogo"
                      sx={{
                        justifyContent: 'center',
                        width: 'auto',
                        height: '80px'
                      }}
                    />
                  </Link>
                </Box>
                <Box display="flex" justifyContent="flex-end">
                  <SearchWrapper
                    sx={{ flexGrow: 0, display: { xs: 'none', md: 'flex' } }}
                  >
                    <SearchIconWrapper>
                      <SearchIcon color="secondary" />
                    </SearchIconWrapper>
                    <SearchInputBase
                      placeholder="Buscar..."
                      value={search}
                      inputProps={{ 'aria-label': 'Búsqueda' }}
                      onKeyPress={sendSearch}
                      onChange={handleSearch}
                    />
                  </SearchWrapper>
                  <Box
                    sx={{ flexGrow: 0, display: { xs: 'none', md: 'flex' } }}
                  >
                    <Tooltip
                      title="Cuenta usuario"
                      placement="bottom"
                      TransitionComponent={Fade}
                      TransitionProps={{ timeout: 600 }}
                    >
                      <IconButton
                        sx={{
                          padding: '10px 12px 8px'
                        }}
                      >
                        <Link
                          to={userData === null ? '/login' : '/user-account'}
                        >
                          <PersonIcon color="secondary" />
                        </Link>
                      </IconButton>
                    </Tooltip>
                    <CartWidget
                      productsInCart={cart?.length}
                      switchSideCart={switchSideCart}
                    />
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  width: '100%',
                  flexWrap: 'wrap',
                  justifyContent: 'center'
                }}
              >
                <Divider orientation="vertical" variant="middle" flexItem />
                {categories?.map(({ idCategory, name, routeURL }) => {
                  return (
                    <Fragment key={idCategory}>
                      <Button
                        component={Link}
                        to={routeURL}
                        key={name}
                        color="secondary"
                        sx={{ display: 'block', padding: '4px 10px' }}
                        onClick={resetFilter}
                      >
                        {name}
                      </Button>
                      <Divider
                        orientation="vertical"
                        variant="middle"
                        flexItem
                      />
                    </Fragment>
                  )
                })}
              </Box>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              width="100%"
              alignItems="center"
              sx={{ display: { xs: 'flex', md: 'none' } }}
            >
              <Box display="flex">
                <Menu
                  id="menu-appbar"
                  anchorReference="anchorPosition"
                  anchorPosition={{ top: 56, left: 0 }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}
                  open={Boolean(anchorElNav)}
                  onClose={toggleNavMenu}
                  PaperProps={{
                    style: {
                      width: '100vw',
                      maxWidth: '100vw'
                    }
                  }}
                  MenuListProps={{ disablePadding: true }} //quitar padding de menu hamburguesa
                  style={{
                    left: '-16px',
                    marginTop: '62px'
                  }}
                  sx={{ display: { xs: 'block', md: 'none' } }}
                >
                  <SearchWrapper
                    sx={{
                      display: { xs: 'flex', md: 'none' },
                      flexGrow: 1
                    }}
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(230,230,230,1) 0%, rgba(247,247,247,1) 100%)'
                    }}
                  >
                    <SearchIconWrapper>
                      <SearchIcon color="secondary" />
                    </SearchIconWrapper>
                    <SearchInputBase
                      fullWidth
                      value={search}
                      placeholder="Buscar..."
                      inputProps={{ 'aria-label': 'Búsqueda' }}
                      onChange={handleSearch}
                      onKeyPress={sendSearch}
                      onKeyDown={(e) => e.stopPropagation()} //evitar el focus a MenuItem con teclas
                    />
                  </SearchWrapper>
                  {categories && (
                    <MobileMenu
                      categories={categories}
                      toggleNavMenu={toggleNavMenu}
                      expanded={expanded}
                      handleChange={handleChange}
                    />
                  )}
                  <Box sx={{ mt: 3 }}>
                    <Button
                      startIcon={<PersonIcon />}
                      fullWidth
                      sx={{
                        display: 'flex',
                        justifyContent: 'start',
                        fontFamily: 'Quicksand',
                        color: '#292929',
                        paddingLeft: '16px',
                        marginBottom: '0.5rem',
                        textTransform: 'none'
                      }}
                    >
                      <Link
                        to={userData === null ? '/login' : '/user-account'}
                        className="user-account"
                      >
                        Mi Cuenta
                      </Link>
                    </Button>
                  </Box>
                </Menu>
                <Tooltip title="Menú">
                  <IconButton
                    size="large"
                    aria-label="menu"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    color="inherit"
                    onClick={toggleNavMenu}
                  >
                    {anchorElNav ? <CloseIcon /> : <MenuIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
              <Box
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  width: 'fit-content'
                }}
              >
                <Link to="/">
                  <Avatar
                    alt="Logo de Grupo Monaco"
                    variant="square"
                    src={logo}
                    className="navbarLogo"
                    sx={{
                      justifyContent: 'center',
                      width: 'auto',
                      height: '54px'
                    }}
                  />
                </Link>
              </Box>
              <CartWidget
                productsInCart={cart?.length}
                switchSideCart={switchSideCart}
              />
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      {showOffset && <Offset height={offset} />}
    </Fragment>
  )
}

export default Navbar
